import { CARD_PADDING_RIGHT, CARD_PADDING_TOP_DESKTOP, MediaCard } from '../MediaCard';
import { MediaPostListItem } from '../../types';
import { MASONRY_CONTAINER, MASONRY_SPACER } from '../../react-hooks/useMasonryGroupResize';
import { tw } from '../../styles/tw';

declare module 'react' {
  interface CSSProperties {
    '--card-padding-right'?: string;
    '--height'?: string;
    '--card-padding-top-desktop'?: string;
    '--order'?: string;
  }
}

interface Props {
  articles: MediaPostListItem[];
  maxColumns?: 3 | 4;
  className?: string;
}

const MAX_ARTICLE_SIZE = 652;

const Masonry = ({ articles, maxColumns = 3, className }: Props): JSX.Element => {
  const masonryCard = maxColumns === 3 ? masonryCard3Cols : masonryCard4Cols;

  return (
    <div
      style={{
        '--card-padding-right': `${CARD_PADDING_RIGHT / 16}rem`,
        '--height': `${Math.ceil((articles.length / 2) * MAX_ARTICLE_SIZE) / 16}rem`,
      }}
      className={tw(
        'w-full md:flex md:h-[var(--height)] md:flex-col md:flex-wrap md:gap-x-[var(--card-padding-right)]',
        className,
        MASONRY_CONTAINER,
      )}
    >
      <MasonrySpacer key={2} order={2} maxColumns={maxColumns} />
      <MasonrySpacer key={3} order={3} maxColumns={maxColumns} />
      {maxColumns === 4 ? <MasonrySpacer key={4} order={4} maxColumns={maxColumns} /> : null}
      {articles.map((article) => (
        <MediaCard className={masonryCard} key={article.id} article={article} />
      ))}
      {articles.length < 2 && <div className={tw('hidden md:block', masonryCard)} />}
      {articles.length < 3 && <div className={tw('hidden md:block', masonryCard)} />}
      {articles.length < 4 && <div className={tw('hidden md:block', masonryCard)} />}
    </div>
  );
};

const MasonrySpacer = ({ order, maxColumns }: { order: number; maxColumns: 3 | 4 }) => (
  <div
    style={{
      '--order': String(order),
      '--card-padding-top-desktop': `${CARD_PADDING_TOP_DESKTOP / 16}rem`,
    }}
    className={tw(
      'order-[var(--order)] mt-[var(--card-padding-top-desktop)] hidden w-px basis-full bg-blood',
      maxColumns === 4 ? '2xl:block' : 'xl:block',
      maxColumns === 4 && order < 4 && 'xl:block',
      order < 3 && 'md:block',
      MASONRY_SPACER,
    )}
  />
);

const masonryCard3Cols = tw(
  'md:w-[calc((100%_-_2*var(--card-padding-right)_-_1px)/2)] xl:w-[calc((100%_-_4*var(--card-padding-right)_-_2px)/3)] md:[&:nth-child(2n+3)]:order-1 md:[&:nth-child(2n+4)]:order-2 xl:[&:nth-child(3n+3)]:order-1 xl:[&:nth-child(3n+4)]:order-2 xl:[&:nth-child(3n+5)]:order-3',
);

const masonryCard4Cols = tw(
  'md:w-[calc((100%_-_2*var(--card-padding-right)_-_1px)/2)] xl:w-[calc((100%_-_4*var(--card-padding-right)_-_2px)/3)] 2xl:w-[calc((100%_-_6*var(--card-padding-right)_-_3px)/4)] md:[&:nth-child(2n+4)]:order-1 md:[&:nth-child(2n+5)]:order-2 xl:[&:nth-child(3n+4)]:order-1 xl:[&:nth-child(3n+5)]:order-2 xl:[&:nth-child(3n+6)]:order-3 2xl:[&:nth-child(4n+4)]:order-1 2xl:[&:nth-child(4n+5)]:order-2 2xl:[&:nth-child(4n+6)]:order-3 2xl:[&:nth-child(4n+7)]:order-4',
);

export { Masonry, MAX_ARTICLE_SIZE };
